import { Injectable, signal, computed } from '@angular/core';
import type { BannerConfig } from '@shared/models/banner.model';

/**
 * BannerStore
 * Centralized signal-based state management for banners
 *
 * @description
 * Manages banner state using Angular Signals for reactive updates.
 * Provides methods to add, remove, and clear banners.
 *
 * Features:
 * - Signal-based reactivity (Angular 17+)
 * - Immutable state updates
 * - Computed properties for derived state
 * - Supports multiple simultaneous banners
 *
 * @example
 * ```typescript
 * const bannerId = bannerStore.addBanner({
 *   type: 'success',
 *   title: 'Export effectué',
 *   message: 'Fichier envoyé à votre email',
 *   duration: 8000,
 * });
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class BannerStore {
  // Private state signal
  private readonly _banners = signal<BannerConfig[]>([]);

  // Public readonly signals
  readonly banners = this._banners.asReadonly();

  // Computed: Check if any banners are visible
  readonly hasVisibleBanners = computed(() => this._banners().length > 0);

  // Computed: Count of active banners
  readonly bannerCount = computed(() => this._banners().length);

  /**
   * Add a banner to the state
   * Generates unique ID and adds to banners array
   *
   * @param config - Banner configuration without ID
   * @returns Generated banner ID
   */
  addBanner(config: Omit<BannerConfig, 'id'>): string {
    const id = `banner-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
    const newBanner: BannerConfig = {
      id,
      dismissible: true, // Default to true
      ...config,
    };

    this._banners.update((banners) => [...banners, newBanner]);
    return id;
  }

  /**
   * Remove a specific banner by ID
   *
   * @param id - Banner ID to remove
   */
  removeBanner(id: string): void {
    this._banners.update((banners) => banners.filter((banner) => banner.id !== id));
  }

  /**
   * Clear all banners
   */
  clearAll(): void {
    this._banners.set([]);
  }

  /**
   * Get a specific banner by ID
   *
   * @param id - Banner ID
   * @returns Banner config or undefined
   */
  getBanner(id: string): BannerConfig | undefined {
    return this._banners().find((banner) => banner.id === id);
  }
}
